import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { gql, useMutation } from '@apollo/client';
import Container from 'ls-common-client/dist/components/Container';
import EmptyButton from 'ls-common-client/dist/components/EmptyButton';
import NotificationSlider from 'ls-common-client/dist/components/NotificationSlider';
import Icon from 'ls-common-client/dist/components/Icon';
import Image from 'ls-common-client/dist/components/Image';
import { Context } from '../../context/AppContext';
import saveAdded from '../../assets/userManagement/saveAdded.svg';
import saveRemoved from '../../assets/userManagement/saveRemoved.svg';

const growAnimation = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(0.9);
  }
  80% {
    transform: scale(0.6);
  }
  100% {
    transform: scale(1);
  }
`;

const shrinkAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  70% {
    transform: scale(0.1);
  }
  90% {
    transform: scale(0.3);
  }
  100% {
    transform: scale(0);
  }
`;

const StyledContainer = styled(Container)`
  ${props =>
    props.grow
      ? css`
          animation-name: ${growAnimation};
        `
      : ''}
  ${props =>
    props.shrink
      ? css`
          animation-name: ${shrinkAnimation};
        `
      : ''}
animation-duration: 0.5s;
  animation-timing-function: linear;
  height: 21px;
  transform: ${props => (props.isFavourite ? 'scale(1)' : 'scale(0)')};
`;

const notificationButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.primary.normal};
  font-size: 16px;
  text-align: center;
  & i {
    opacity: ${props => (props.istouch ? '1' : '0')};
    transition: all ease 0.3s;
    margin-bottom: -4px;
  }
  &:hover {
    i {
      opacity: 1;
      transform: translateX(5px);
    }
  }
`;

const StyledEmptyButton = styled(EmptyButton)`
  ${notificationButtonStyles}
`;

const StyledLink = styled(Link)`
  ${notificationButtonStyles}
`;

const addFavouriteMutation = gql`
  mutation addFavourite($input: AddFavouriteInput!) {
    addFavourite(input: $input) {
      favourite {
        id
      }
    }
  }
`;

const removeFavouriteMutation = gql`
  mutation removeFavourite($input: RemoveFavouriteInput!) {
    removeFavourite(input: $input) {
      success
    }
  }
`;

const defaultRender = ({ onClick, isFavourite, grow, shrink, ...props }) => (
  <EmptyButton
    backgroundColor="white"
    borderRadius="100%"
    minWidth="33px"
    minHeight="33px"
    position="relative"
    boxShadow="0 0 7px 0 rgba(0, 0, 0, 0.1)"
    display="flex"
    alignItems="center"
    justifyContent="center"
    onClick={onClick}
    {...props}
  >
    <Icon
      fontSize="18px"
      iconColor="normal"
      className="ls-icon icon-heartoutline"
    />
    <Container
      position="absolute"
      left="50%"
      top="50%"
      transform="translate(-50%,-50%)"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <StyledContainer grow={grow} shrink={shrink} isFavourite={isFavourite}>
        <Icon
          fontSize="19px"
          iconColor="#f16159"
          className="ls-icon icon-heartsolid"
          marginTop="1px"
        />
      </StyledContainer>
    </Container>
  </EmptyButton>
);

const SaveButton = props => {
  const {
    children,
    delayRemove = false,
    favourites,
    profile,
    ...otherProps
  } = props;
  const {
    media: { mobile },
    userAgent: { isTouch },
    auth,
    user,
  } = useContext(Context);
  const { state, actions } = favourites || {};
  const [showAddSuccess, setShowAddSuccess] = useState();
  const [showRemoveSuccess, setShowRemoveSuccess] = useState();
  const [showReachedMaximum, setShowReachedMaximum] = useState();
  const [timeoutId, setTimeoutId] = useState();
  const [grow, setGrow] = useState();
  const [shrink, setShrink] = useState();

  const profileInState = (state || []).find(({ id }) => id === profile.id);
  const isFavourite = profileInState && !profileInState.removed;
  const { user: currentUser } = user || {};
  const { id: userId } = currentUser || {};

  const [addFavourite] = useMutation(addFavouriteMutation);
  const [removeFavourite] = useMutation(removeFavouriteMutation);

  useEffect(() => {
    if (isFavourite) {
      setGrow(true);
    }
  }, [isFavourite]);

  // On the Saved Favourites page, a delay is required when removing a saved profile to allow for the notifications bar to appear,
  // which provides the user with the opportunity to undo the removal. Otherwise the profile will immediately disappear from the
  // users screen as soon as they unsave the profile. setTimeout is used to delay the removal for the duration that it takes for
  // the notifications bar to disappear. If the user clicks undo during this time, the timeout is cleared so that the profile is
  // not removed.
  useEffect(() => {
    if (profileInState && profileInState.removed) {
      if (delayRemove) {
        setTimeoutId(
          setTimeout(() => {
            if (profileInState.removed) {
              actions.removeFromFavourites(profileInState.id);
            }
          }, 6000)
        );
      } else {
        actions.removeFromFavourites(profileInState.id);
      }
    }
  }, [profileInState]);

  const onClick = async e => {
    e.preventDefault();

    if (!userId) {
      auth.login({ redirectUrl: window.location.pathname });
      return;
    }

    if (isFavourite) {
      await removeFavourite({
        variables: {
          input: {
            userId,
            listingId: profileInState.id,
          },
        },
      });
      setShrink(true);
      setGrow(false);
      setShowRemoveSuccess(true);
      actions.setRemovedStatus(profileInState.id);
    } else if (state.length >= 20) {
      setShowReachedMaximum(true);
    } else {
      await addFavourite({
        variables: { input: { userId, listingId: profile.id } },
      });
      const {
        displayName,
        id,
        media,
        postcode,
        reviewAggregates,
        stateCode,
        suburb,
        vanityUrlPath,
      } = profile || {};
      actions.addToFavourites({
        displayName,
        id,
        media,
        postcode,
        reviewAggregates,
        stateCode,
        suburb,
        vanityUrlPath,
      });
      setShrink(false);
      setGrow(true);
      setShowAddSuccess(true);
      setShowRemoveSuccess(false);
      clearTimeout(timeoutId);
    }
  };

  return (
    <>
      {children
        ? children({ onClick, isFavourite, grow, shrink, ...otherProps })
        : defaultRender({ onClick, isFavourite, grow, shrink, ...otherProps })}
      <NotificationSlider
        mode={mobile ? 'mobile' : 'desktop'}
        show={showAddSuccess && !delayRemove}
        icon={
          <Container padding={mobile ? '30px 10px 10px 10px' : '10px'}>
            <Image src={saveAdded} />
          </Container>
        }
        onClose={() => setShowAddSuccess(false)}
        heading="Profile has been successfully saved."
        text={
          <Container
            display="flex"
            justifyContent={mobile ? 'center' : 'flex-start'}
            marginBottom={mobile ? '30px' : '0'}
          >
            <StyledLink to="/saved" istouch={isTouch ? 1 : 0}>
              Go to saved
              <Icon className="ls-icon icon-generalarrowelarge" />
            </StyledLink>
          </Container>
        }
        zIndex="9999"
      />
      <NotificationSlider
        mode={mobile ? 'mobile' : 'desktop'}
        show={showRemoveSuccess}
        icon={
          <Container padding={mobile ? '30px 10px 10px 10px' : '10px'}>
            <Image src={saveRemoved} />
          </Container>
        }
        onClose={() => setShowRemoveSuccess(false)}
        heading="Profile has been successfully removed."
        text={
          <Container
            display="flex"
            justifyContent={mobile ? 'center' : 'flex-start'}
            marginBottom={mobile ? '30px' : '0'}
          >
            <StyledEmptyButton onClick={onClick} istouch={isTouch ? 1 : 0}>
              Undo
              <Icon className="ls-icon icon-generalarrowelarge" />
            </StyledEmptyButton>
          </Container>
        }
        zIndex="9999"
      />
      <NotificationSlider
        mode={mobile ? 'mobile' : 'desktop'}
        show={showReachedMaximum}
        icon={
          <Container padding={mobile ? '30px 10px 10px 10px' : '10px'}>
            <Image src={saveRemoved} />
          </Container>
        }
        onClose={() => showReachedMaximum(false)}
        heading="You have reached the maximum number of saved profiles."
        text={
          <Container
            display="flex"
            justifyContent={mobile ? 'center' : 'flex-start'}
            marginBottom={mobile ? '30px' : '0'}
          >
            <StyledEmptyButton
              onClick={() => setShowReachedMaximum(false)}
              istouch={isTouch ? 1 : 0}
            >
              OK
            </StyledEmptyButton>
          </Container>
        }
        zIndex="9999"
      />
    </>
  );
};

SaveButton.propsTypes = {
  children: PropTypes.func,
  favourites: PropTypes.shape({
    state: PropTypes.arrayOf(PropTypes.object),
    actions: PropTypes.object,
  }).isRequired,
  listingId: PropTypes.string,
  userId: PropTypes.string,
};

SaveButton.defaultProps = {
  children: defaultRender,
  listingId: null,
  userId: null,
};

export default SaveButton;
